"use client"

import { Card, CardContent } from "@/components/ui/card"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { AlertCircle, Shield, FileText, Users, ShoppingCart, Heart } from "lucide-react"
import { HeroSection } from "@/components/ui/hero-section"
import Link from "next/link"
import { Button } from "@/components/ui/button"

export function LegalContent() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative px-4 pt-6 md:pt-8 md:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-2">
            <BreadcrumbComponent items={[{ label: "Home", href: "/" }, { label: "Legal Information" }]} />
          </div>

          <HeroSection
            size="minimal"
            showSparkle={false}
            showGlow={false}
            title="Legal Information"
            subtitle="Important legal information, disclaimers, and policies for What Grass Is This? users"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
            {/* Navigation Sidebar */}
            <div className="hidden lg:block lg:col-span-1">
              <Card className="bg-white/95 backdrop-blur-xs border-green-200 shadow-xl sticky top-8">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-green-600" />
                    Legal Documents
                  </h3>
                  <nav className="space-y-2">
                    <a href="#affiliate-disclaimer" className="block p-3 rounded-lg hover:bg-green-50 text-gray-700 hover:text-green-700 transition-colors">
                      <ShoppingCart className="w-4 h-4 inline mr-2" />
                      Affiliate Disclaimer
                    </a>
                    <Link href="/terms" className="block p-3 rounded-lg hover:bg-green-50 text-gray-700 hover:text-green-700 transition-colors">
                      <Shield className="w-4 h-4 inline mr-2" />
                      Terms of Service
                    </Link>
                    <Link href="/privacy" className="block p-3 rounded-lg hover:bg-green-50 text-gray-700 hover:text-green-700 transition-colors">
                      <Users className="w-4 h-4 inline mr-2" />
                      Privacy Policy
                    </Link>
                    <a href="#ai-disclaimer" className="block p-3 rounded-lg hover:bg-green-50 text-gray-700 hover:text-green-700 transition-colors">
                      <Heart className="w-4 h-4 inline mr-2" />
                      AI Disclaimer
                    </a>
                  </nav>
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Affiliate Disclaimer */}
              <Card className="bg-white/95 backdrop-blur-xs border-green-200 shadow-xl">
                <CardContent className="p-4 sm:p-6 md:p-8">
                  <div className="prose prose-lg max-w-none">
                    <div id="affiliate-disclaimer" className="scroll-mt-20">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                        <ShoppingCart className="w-6 h-6 mr-3 text-green-600" />
                        Affiliate Disclaimer (FTC Required)
                      </h2>
                      
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
                        <div className="flex items-start">
                          <AlertCircle className="w-6 h-6 text-yellow-600 mr-3 mt-1 flex-shrink-0" />
                          <div>
                            <h3 className="text-lg font-semibold text-yellow-900 mb-2">FTC Compliance Notice</h3>
                            <p className="text-yellow-800">
                              This disclosure is required by the Federal Trade Commission (FTC) to ensure transparency about our affiliate relationships.
                            </p>
                          </div>
                        </div>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-4">What Are Affiliate Links?</h3>
                      <p className="text-gray-700 mb-6">
                        Affiliate links are special links that allow us to earn a small commission when you make a purchase through our recommendations. 
                        These links help support our service and allow us to continue providing free lawn care advice and tools.
                      </p>

                      <h3 className="text-xl font-bold text-gray-900 mb-4">Our Affiliate Relationships</h3>
                      <p className="text-gray-700 mb-6">
                        What Grass Is This? may receive compensation from the following types of affiliate partnerships:
                      </p>
                      <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                        <li><strong>Lawn care products:</strong> Fertilizers, seeds, herbicides, and other lawn care supplies</li>
                        <li><strong>Garden tools and equipment:</strong> Mowers, trimmers, sprinklers, and other equipment</li>
                        <li><strong>Educational resources:</strong> Books, courses, and other lawn care learning materials</li>
                        <li><strong>Professional services:</strong> Lawn care services and consultations (where applicable)</li>
                      </ul>

                      <h3 className="text-xl font-bold text-gray-900 mb-4">Transparency Commitment</h3>
                      <p className="text-gray-700 mb-6">
                        We are committed to full transparency about our affiliate relationships:
                      </p>
                      <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                        <li>We only recommend products we genuinely believe will help your lawn</li>
                        <li>Our recommendations are based on research, user feedback, and expert knowledge</li>
                        <li>Affiliate compensation does not influence our product recommendations</li>
                        <li>We clearly disclose affiliate relationships when required by law</li>
                        <li>You pay the same price whether you use our affiliate links or not</li>
                      </ul>

                      <h3 className="text-xl font-bold text-gray-900 mb-4">How to Identify Affiliate Links</h3>
                      <p className="text-gray-700 mb-6">
                        Affiliate links on our platform may be identified by:
                      </p>
                      <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                        <li>Clear disclosure notices near product recommendations</li>
                        <li>Text indicating "We may earn a commission" or similar language</li>
                        <li>Special formatting or badges on affiliate-linked products</li>
                        <li>URLs that contain affiliate tracking parameters</li>
                      </ul>

                      <h3 className="text-xl font-bold text-gray-900 mb-4">Your Rights and Choices</h3>
                      <p className="text-gray-700 mb-6">
                        As a user, you have the right to:
                      </p>
                      <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                        <li>Choose whether to use our affiliate links or find products independently</li>
                        <li>Know when we have a financial relationship with a product or service</li>
                        <li>Receive honest, unbiased recommendations regardless of affiliate status</li>
                        <li>Contact us with questions about our affiliate relationships</li>
                      </ul>

                      <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                      <p className="text-gray-700 mb-6">
                        If you have questions about our affiliate relationships or this disclosure, please contact us:
                      </p>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-700">
                          <strong>Email:</strong>{" "}
                          <a href="mailto:support@whatgrassisthis.com" className="text-green-600 hover:text-green-700 font-semibold">
                            support@whatgrassisthis.com
                          </a>
                        </p>
                        <p className="text-gray-700">
                          <strong>Subject:</strong> Affiliate Disclosure Inquiry
                        </p>
                      </div>

                      <div className="mt-8 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800">
                          <strong>Last Updated:</strong> March 25, 2026
                        </p>
                        <p className="text-sm text-green-800 mt-2">
                          This affiliate disclaimer complies with FTC guidelines and is regularly reviewed and updated as needed.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* AI Disclaimer */}
              <Card className="bg-white/95 backdrop-blur-xs border-green-200 shadow-xl">
                <CardContent className="p-4 sm:p-6 md:p-8">
                  <div className="prose prose-lg max-w-none">
                    <div id="ai-disclaimer" className="scroll-mt-20">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                        <Heart className="w-6 h-6 mr-3 text-green-600" />
                        AI Analysis Disclaimer
                      </h2>
                      
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
                        <div className="flex items-start">
                          <AlertCircle className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                          <div>
                            <h3 className="text-lg font-semibold text-blue-900 mb-2">AI Technology Notice</h3>
                            <p className="text-blue-800">
                              Our grass identification and lawn analysis features use artificial intelligence technology that provides estimates and recommendations.
                            </p>
                          </div>
                        </div>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-4">AI Analysis Limitations</h3>
                      <p className="text-gray-700 mb-6">
                        While our AI technology is advanced, it has certain limitations:
                      </p>
                      <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                        <li>Results are estimates based on visual analysis and may not be 100% accurate</li>
                        <li>Image quality, lighting, and angle can affect identification accuracy</li>
                        <li>AI recommendations should be considered as guidance, not definitive advice</li>
                        <li>Local conditions and climate factors may require adjustments to recommendations</li>
                      </ul>

                      <h3 className="text-xl font-bold text-gray-900 mb-4">Professional Consultation</h3>
                      <p className="text-gray-700 mb-6">
                        We recommend consulting with local lawn care professionals for:
                      </p>
                      <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                        <li>Complex lawn problems or diseases</li>
                        <li>Major lawn renovations or installations</li>
                        <li>Local climate-specific advice</li>
                        <li>Professional lawn care services</li>
                      </ul>

                      <h3 className="text-xl font-bold text-gray-900 mb-4">User Responsibility</h3>
                      <p className="text-gray-700 mb-6">
                        Users are responsible for:
                      </p>
                      <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                        <li>Verifying AI recommendations against local conditions</li>
                        <li>Testing products on small areas before full application</li>
                        <li>Following product labels and manufacturer instructions</li>
                        <li>Seeking professional help for complex issues</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Links */}
              <Card className="bg-linear-to-br from-green-50 to-emerald-50 border-green-200 shadow-xl">
                <CardContent className="p-4 sm:p-6 md:p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Other Legal Documents</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Button asChild variant="outline" className="w-full justify-start h-auto p-4 border-green-200 hover:bg-green-50">
                      <Link href="/terms">
                        <Shield className="w-5 h-5 mr-3 text-green-600" />
                        <div className="text-left">
                          <div className="font-semibold text-gray-900">Terms of Service</div>
                          <div className="text-sm text-gray-600">Usage terms and conditions</div>
                        </div>
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full justify-start h-auto p-4 border-green-200 hover:bg-green-50">
                      <Link href="/privacy">
                        <Users className="w-5 h-5 mr-3 text-green-600" />
                        <div className="text-left">
                          <div className="font-semibold text-gray-900">Privacy Policy</div>
                          <div className="text-sm text-gray-600">Data collection and usage</div>
                        </div>
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 
